import { NgStyle, NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, ElementRef, input, viewChild } from '@angular/core'

export interface FrameAvatar {
  img: string
  score: number | null
  isFrame?: boolean
}
@Component({
  selector: 'frame-avatar',
  imports: [NgStyle, NgClass],
  standalone: true,
  templateUrl: './frame-avatar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FrameAvatarComponent {
  data = input.required<FrameAvatar>()
  maxWidth = input<string>()

  score = computed(() => {
    const raw = this.data().score
    if (raw === null) return 0

    const clamped = Math.max(0, Math.min(100, Number(raw)))
    return Number(clamped.toFixed(0))
  })

  avatar = viewChild<ElementRef<HTMLImageElement>>('avatar')

  getBorderColor(score: number | null): string {
    if (score === null) return '#8C2323'

    if (score <= 31) return '#8C2323'
    if (score <= 79) return '#FFBB38'
    return '#00B575'
  }

  scoreLevel(): number {
    const score = this.score()

    if (score === null) return 0

    if (score <= 31) return 1
    if (score <= 79) return 2
    return 3
  }

  scoreColor(): 'red' | 'yellow' | 'green' {
    const score = this.score()
    if (score === null) return 'red'

    if (score <= 31) return 'red'
    if (score <= 79) return 'yellow'
    return 'green'
  }

  getShadowColor(score: number | null): string {
    if (score === null) return '1px 1px 4.9px 0px #FF10036B'
    if (score <= 31) return '1px 1px 4.9px 0px #FF10036B'
    if (score <= 79) return '1px 1px 4.9px 0px #FFDD036B'
    return '1px 1px 4.9px 0px #03FF706B'
  }

  getSegmentWidth(score: number | null, start: number): string {
    if (score === null) return '0px'

    const segment = Math.min(score - start, 25)
    return `${(segment / 25) * this.avatar()!.nativeElement.offsetWidth}px` // 120 es el ancho total
  }

  getSegmentHeight(score: number | null, start: number): string {
    if (score === null) return '0px'

    const segment = Math.min(score - start, 25)
    return `${(segment / 25) * this.avatar()!.nativeElement.offsetHeight}px` // 140 es el alto total
  }

  getOffset(score: number | null, start: number): string {
    if (score === null) return '0px'

    const segment = Math.min(score - start, 25)
    const offset = ((25 - segment) / 25) * this.avatar()!.nativeElement.offsetHeight
    return `${offset}px`
  }

  getScoreBubbleStyle(score: number | null): Record<string, string> {
    if (score === null) score = 0
    if (score > 100) score = 100

    const style: Record<string, string> = {
      background: this.getBorderColor(score),
      boxShadow: this.getShadowColor(score)
    }

    // Simple positioning: always top-right corner with safe margins
    style['top'] = '4px'
    style['right'] = '4px'

    return style
  }
}
